//#region ENUM ScrollingTypeEnum
/**
 * ### ScrollingTypeEnum
 * Enum representing different scrolling behavior types for list rendering.
 *
 * This enum distinguishes between two common scrolling strategies used for
 * rendering lists efficiently in Angular applications, particularly when
 * dealing with Firestore data fetching.
 *
 * 🔁 **Infinite Scroll**
 * - **Mechanism:** Loads more data when the user scrolls near the end of the list.
 * - **Implementation:** Uses IntersectionObserver or scroll events to detect bottom reach.
 * - **Data Fetching:** Fetches more pages from Firestore (e.g., using startAfter).
 * - **Memory Usage:** Increases as more items are loaded (older items remain in DOM).
 * - **Best For:** Items with dynamic height (e.g., accordions, expandable cards).
 * - **Drawback:** May grow memory usage as DOM size increases.
 * - **Example Component:** FirestoreInfiniteScrollComponent
 * - **Use when:** Your items don’t have fixed height, you want natural scrolling, or need expandable/interactive items.
 *
 * 📜 **Virtual Scroll**
 * - **Mechanism:** Only renders visible items plus buffer, and reuses DOM elements.
 * - **Implementation:** Uses cdk-virtual-scroll-viewport from Angular CDK.
 * - **Data Fetching:** Usually loads all data ahead or batches it in fixed ranges.
 * - **Memory Usage:** Very low — only visible DOM elements are kept.
 * - **Best For:** Items with fixed height (required).
 * - **Drawback:** Doesn’t support dynamic height (e.g., expanding accordions will break it).
 * - **Example Component:** FirestoreVirtualScrollComponent
 * - **Use when:** Your items have consistent fixed height, want maximum performance with large lists, and don’t need dynamic expansion/collapse.
 *
 * 🧠 **Summary Table**
 * | Feature          | Infinite Scroll                         | Virtual Scroll                                  |
 * |------------------|---------------------------------------|------------------------------------------------|
 * | Scroll behavior  | Native scroll + load more              | Only visible items rendered                      |
 * | DOM memory usage | Grows with data                       | Constant / minimal                               |
 * | Data source      | Paged Firestore calls                  | All-at-once or windowed from memory             |
 * | Item height      | Dynamic (✅ works with accordions)    | Fixed (❌ breaks with accordions)                |
 * | Ideal for       | Feeds, expandable items                 | Large tables, flat lists with fixed size        |
 *
 * @enum {string}
 * @property INFINITE - Your items don’t have fixed height; supports natural scrolling and expandable or interactive items.
 * @property VIRTUAL - Your items have a consistent fixed height; provides maximum performance with very large lists, but no dynamic expansion/collapse.
 *
 * @example
 * const scrollType: ScrollingTypeEnum = ScrollingTypeEnum.INFINITE;
 */
export enum ScrollingTypeEnum {
  /** Your items don’t have fixed height; supports natural scrolling and expandable or interactive items. */
  INFINITE = 'INFINITE',

  /** Your items have a consistent fixed height; provides maximum performance with very large lists, but no dynamic expansion/collapse. */
  VIRTUAL = 'VIRTUAL',
}

//#endregion

//#region ENUM ScrollingTypeEnumHumanReadable
/**
 * ### ScrollingTypeEnumHumanReadable
 * Enum representing human-readable labels for ScrollingTypeEnum values.
 *
 * @enum {string}
 * @property INFINITE - Label for infinite scrolling type.
 * @property VIRTUAL - Label for virtual scrolling type.
 *
 * @example
 * const label: ScrollingTypeEnumHumanReadable = ScrollingTypeEnumHumanReadable.INFINITE;
 */
export enum ScrollingTypeEnumHumanReadable {
  /** Label for infinite scrolling type. */
  INFINITE = 'Infinite',

  /** Label for virtual scrolling type. */
  VIRTUAL = 'Virtual',
}

//#endregion

//#region TYPE ScrollingTypeType
/**
 * ### ScrollingTypeType
 * Type representing the keys of ScrollingTypeEnum.
 *
 * @typedef {keyof typeof ScrollingTypeEnum} ScrollingTypeType
 *
 * @example
 * const scrollKey: ScrollingTypeType = 'INFINITE';
 */
export type ScrollingTypeType = keyof typeof ScrollingTypeEnum;
//#endregion

//#region INTERFACE IScrollDataInput

/**
 * ### IScrollDataInput
 * Generic interface representing the input structure for scrollable components that consume data.
 *
 * @interface IScrollDataInput
 * @extends Object
 * @template DataType - The type of the data being passed into the scrollable component.
 * @defaults Not applicable
 * @description This interface is designed to wrap scroll-related data in a consistent structure.
 *              It enables generic scroll components (e.g., infinite or virtual scrollers) to accept
 *              typed data while maintaining flexibility across different use cases.
 *              Typically used to encapsulate a single data payload being scrolled or displayed.
 *
 * @example
 * const scrollInput: IScrollDataInput<User[]> = {
 *   data: [{ name: 'Alice' }, { name: 'Bob' }]
 * };
 */
export interface IScrollDataInput<DataType> extends Object {
  /**
   * The actual data payload to be rendered or processed by the scrollable component.
   * @description Can be any structure (array, object, etc.) depending on how the scroll component is designed.
   *              This enables the scroll wrapper to remain agnostic of specific data formats.
   *
   * @type {DataType}
   * @template DataType
   * @example
   * // For an array of items:
   * const input: IScrollDataInput<string[]> = {
   *   data: ['item1', 'item2', 'item3']
   * };
   */
  data: DataType;
}

//#endregion
